"use client";

import { useState, useCallback, useMemo, Suspense } from "react";
import dynamic from "next/dynamic";
import {
  GraduationCap,
  Settings2,
  Copy,
  Download,
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  FileText,
  Sparkles,
  Brain,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SolutionSteps } from "./components/SolutionSteps";
import { parseSolutionSteps } from "@/lib/math-engine/solution-steps";
import { getEducationSystemNames } from "@/lib/math-taxonomy/education-systems";
import { getLevelNamesForSystem } from "@/lib/math-taxonomy/academic-levels";
import { getTopicsForLevel, getMathTopic } from "@/lib/math-taxonomy/topics";
import { getDifficultyNames } from "@/lib/math-taxonomy/difficulty";
import { getQuestionTypesForLevel } from "@/lib/math-taxonomy/question-types";
import { TOPIC_CAPABILITIES, isTopicImplemented } from "@/lib/math-engine/types";
import { siteConfig } from "@/lib/data/site-config";
import type { EducationSystem, AcademicLevel, MathTopic, Difficulty, QuestionType, GeneratorConfig, GeneratedQuestion } from "@/lib/math-taxonomy/types";
import type { GenerationResult } from "@/lib/math-engine/types";

const MathNotation = dynamic(
  () => import("./components/MathNotation").then((m) => ({ default: m.MathNotation })),
  { ssr: false, loading: () => <span className="text-[var(--color-text-muted)]">Loading notation…</span> },
);

const DEFAULT_CONFIG: GeneratorConfig = {
  educationSystem: "india",
  academicLevel: "class-10",
  topic: "algebra",
  difficulty: "medium",
  questionType: "multiple-choice",
  questionCount: 5,
};

function SelectField({
  label,
  hint,
  value,
  onChange,
  options,
  disabled = false,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="min-w-0">
      <label className="block text-sm font-medium text-[var(--color-text)] mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="min-h-[48px] w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-2.5 text-sm text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30 transition-colors duration-150 disabled:opacity-50 disabled:bg-[var(--color-surface-2)] appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {hint && <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{hint}</p>}
    </div>
  );
}

function ConfigSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4 sm:p-5 border-[var(--color-border)] bg-[var(--color-bg)] hover:border-[var(--color-accent)]/50 transition-colors duration-200">
      <div className="flex items-center gap-2.5 mb-4">
        <Icon className="h-5 w-5 text-[var(--color-accent)]" aria-hidden={true} />
        <h3 className="text-base font-semibold text-[var(--color-text)]">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

function formatSetAsText(
  questions: GeneratedQuestion[],
  meta: { level: string; topic: string; difficulty: string; count: number },
  includeAnswers: boolean,
): string {
  const header = [
    "Mathematics Practice Set",
    "",
    `Level: ${meta.level}`,
    `Topic: ${meta.topic}`,
    `Difficulty: ${meta.difficulty}`,
    `Questions: ${meta.count}`,
    `Suggested time: ${Math.max(5, meta.count * 3)} minutes`,
    `Generated: ${new Date().toLocaleDateString()}`,
    "",
    "--------------------------------",
    "",
  ].join("\n");
  const body = questions
    .map((q, i) => {
      const lines = [`${i + 1}. ${q.question}`];
      const options = q.metadata?.options as string[] | undefined;
      if (q.questionType === "multiple-choice" && options?.length) {
        const labels = ["A", "B", "C", "D"];
        options.slice(0, 4).forEach((opt, oi) => lines.push(`   ${labels[oi]}. ${opt}`));
      }
      if (includeAnswers && q.answer) {
        lines.push(`   Answer: ${q.answer}`);
        const steps = parseSolutionSteps(q.solution);
        steps.forEach((step, si) => lines.push(`   Step ${si + 1}: ${step}`));
      }
      return lines.join("\n");
    })
    .join("\n\n");
  return `${header}${body}\n`;
}

export function MathQuestionGeneratorClient({ initialConfig }: { initialConfig?: Partial<GeneratorConfig> } = {}) {
  const [config, setConfig] = useState<GeneratorConfig>({ ...DEFAULT_CONFIG, ...initialConfig });
  const [questions, setQuestions] = useState<GeneratedQuestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [phase, setPhase] = useState("");
  const [showAnswers, setShowAnswers] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seed, setSeed] = useState(0);
  const [lastStats, setLastStats] = useState<GenerationResult["stats"] | null>(null);

  const levels = useMemo(() => getLevelNamesForSystem(config.educationSystem), [config.educationSystem]);
  const topics = useMemo(
    () => getTopicsForLevel(config.academicLevel).filter((t) => isTopicImplemented(t.id)),
    [config.academicLevel],
  );
  const difficulties = getDifficultyNames();
  const questionTypes = useMemo(() => getQuestionTypesForLevel(config.academicLevel), [config.academicLevel]);
  const caps = TOPIC_CAPABILITIES[config.topic];

  const resolveConfig = useCallback(
    (next: Partial<GeneratorConfig>): GeneratorConfig => {
      const merged = { ...config, ...next };
      const sysLevels = getLevelNamesForSystem(merged.educationSystem);
      const academicLevel = sysLevels.some((l) => l.id === merged.academicLevel)
        ? merged.academicLevel
        : (sysLevels[0]?.id ?? "class-10");
      const levelTopics = getTopicsForLevel(academicLevel).filter((t) => isTopicImplemented(t.id));
      const topic = levelTopics.some((t) => t.id === merged.topic)
        ? merged.topic
        : (levelTopics[0]?.id ?? "algebra");
      const topicCaps = TOPIC_CAPABILITIES[topic];
      const difficulty = topicCaps.difficulties.includes(merged.difficulty) ? merged.difficulty : "medium";
      const availableTypes = getQuestionTypesForLevel(academicLevel)
        .map((q) => q.id)
        .filter((id) => topicCaps.questionTypes.includes(id));
      const questionType = availableTypes.includes(merged.questionType)
        ? merged.questionType
        : availableTypes.includes("mixed")
          ? "mixed"
          : (availableTypes[0] ?? "short-answer");
      const validSubs = new Set((getMathTopic(topic)?.subtopics ?? []).map((s) => s.id));
      const subtopic = merged.subtopic && validSubs.has(merged.subtopic) ? merged.subtopic : undefined;
      return { ...merged, academicLevel, topic, difficulty, questionType, subtopic };
    },
    [config],
  );

  const levelName = useMemo(
    () => levels.find((l) => l.id === config.academicLevel)?.name ?? config.academicLevel,
    [levels, config.academicLevel],
  );
  const topicName = useMemo(
    () => topics.find((t) => t.id === config.topic)?.name ?? config.topic,
    [topics, config.topic],
  );
  const difficultyName = useMemo(
    () => difficulties.find((d) => d.id === config.difficulty)?.name ?? config.difficulty,
    [difficulties, config.difficulty],
  );

  const runGeneration = useCallback(
    async (nextSeed: number, previous: GeneratedQuestion[]) => {
      setIsGenerating(true);
      setError(null);
      try {
        setPhase("Building questions…");
        await new Promise((r) => setTimeout(r, 30));
        const { generateQuestionSet } = await import("@/lib/math-engine/engine");
        setPhase("Checking mathematical validity…");
        await new Promise((r) => setTimeout(r, 0));
        const result = generateQuestionSet(config, { seed: nextSeed, previousQuestions: previous });
        if (result.questions.length === 0) {
          setError(
            "We couldn't generate this set. Try reducing the difficulty, choosing a different topic, or switching question type.",
          );
          return;
        }
        setQuestions(result.questions);
        setLastStats(result.stats);
        setShowAnswers(false);
        setSeed(nextSeed);
      } catch {
        setError("Something went wrong while generating. Please try again.");
      } finally {
        setIsGenerating(false);
        setPhase("");
      }
    },
    [config],
  );

  const handleGenerate = useCallback(() => {
    void runGeneration(seed + 1, []);
  }, [runGeneration, seed]);

  const handleNextSet = useCallback(() => {
    void runGeneration(seed + 1, questions);
  }, [runGeneration, seed, questions]);

  const handleCopy = useCallback(async () => {
    if (questions.length === 0) return;
    const text = formatSetAsText(
      questions,
      { level: levelName, topic: topicName, difficulty: difficultyName, count: questions.length },
      showAnswers,
    );
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Clipboard access was blocked by the browser. Select the text manually to copy.");
    }
  }, [questions, levelName, topicName, difficultyName, showAnswers]);

  const handleDownloadPDF = useCallback(async () => {
    if (questions.length === 0) return;
    try {
      const { downloadWorksheetPdf } = await import("./utils/pdf-generator");
      const qType = questions[0]?.questionType ?? config.questionType;
      await downloadWorksheetPdf(
        questions,
        { levelName, topicName, difficultyName, questionTypeName: qType },
        { includeAnswers: showAnswers },
      );
    } catch {
      setError("PDF generation failed in this browser. Try Copy instead, or use Print → Save as PDF.");
    }
  }, [questions, levelName, topicName, difficultyName, showAnswers, config.questionType]);

  const educationSystemOptions = getEducationSystemNames();
  const levelOptions = levels.map((l) => ({ value: l.id, label: l.name }));
  const topicOptions = topics.map((t) => ({ value: t.id, label: t.name }));
  const subtopicOptions = useMemo(() => {
    const subs = getMathTopic(config.topic)?.subtopics ?? [];
    return [{ value: "", label: "All subtopics" }, ...subs.map((s) => ({ value: s.id, label: s.name }))];
  }, [config.topic]);
  const difficultyOptions = difficulties
    .filter((d) => caps?.difficulties.includes(d.id))
    .map((d) => ({ value: d.id, label: `${d.name} — ${d.description}` }));
  const questionTypeOptions = questionTypes
    .filter((q) => caps?.questionTypes.includes(q.id))
    .map((q) => ({ value: q.id, label: q.name }));

  const primaryAction = questions.length === 0 ? handleGenerate : handleNextSet;
  const primaryLabel = questions.length === 0 ? "Generate Questions" : "Next Set";

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <header className="border-b border-[var(--color-border)] bg-gradient-to-b from-[var(--color-accent-soft)] to-transparent">
        <div className="container mx-auto px-4 py-10 sm:py-14">
          <div className="mx-auto max-w-5xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-accent)] mb-3">
              Mathematics Practice Studio
            </p>
            <h1 className="flex items-center gap-3 text-3xl sm:text-4xl font-bold text-[var(--color-text)]">
              <GraduationCap className="h-9 w-9 flex-shrink-0 text-[var(--color-accent)]" aria-hidden="true" />
              Math Question Generator
            </h1>
            <p className="mt-3 max-w-2xl text-base sm:text-lg text-[var(--color-text-muted)]">
              Build fresh practice sets in seconds — pick your level and topic,
              generate mathematically validated questions with step-by-step solutions,
              then print, copy, or generate another set. All in your browser, no account needed.
            </p>
            <ul className="mt-5 flex flex-wrap gap-2.5" aria-label="Highlights">
              {["100% in your browser", "No account needed", "Step-by-step solutions", "Printable PDF worksheets", "Mathematical notation"].map(
                (chip) => (
                  <li
                    key={chip}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-sm font-medium text-[var(--color-text)]"
                  >
                    <CheckCircle className="h-3.5 w-3.5 text-[var(--color-accent)]" aria-hidden="true" />
                    {chip}
                  </li>
                ),
              )}
            </ul>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 sm:py-14">
        <div className="mx-auto max-w-5xl space-y-8">
          <section aria-label="Configure your practice set">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <ConfigSection title="Where do you study?" icon={GraduationCap}>
                <div className="grid gap-4">
                  <SelectField
                    label="Education system"
                    value={config.educationSystem}
                    onChange={(v) => setConfig(resolveConfig({ educationSystem: v as EducationSystem }))}
                    options={educationSystemOptions.map((opt) => ({ value: opt.id, label: opt.name }))}
                  />
                  <SelectField
                    label="Academic level"
                    value={config.academicLevel}
                    onChange={(v) => setConfig(resolveConfig({ academicLevel: v as AcademicLevel }))}
                    options={levelOptions}
                  />
                </div>
              </ConfigSection>

              <ConfigSection title="What do you want to practise?" icon={Brain}>
                <div className="grid gap-4">
                  <SelectField
                    label="Mathematics topic"
                    value={config.topic}
                    onChange={(v) => setConfig(resolveConfig({ topic: v as MathTopic }))}
                    options={topicOptions}
                  />
                  <SelectField
                    label="Subtopic (optional)"
                    hint="Narrow to one subtopic, or leave open for variety."
                    value={config.subtopic ?? ""}
                    onChange={(v) => setConfig(resolveConfig({ subtopic: v === "" ? undefined : v }))}
                    options={subtopicOptions}
                  />
                </div>
              </ConfigSection>

              <ConfigSection title="How should it challenge you?" icon={Zap}>
                <div className="grid gap-4">
                  <SelectField
                    label="Difficulty"
                    hint="Harder means deeper reasoning, not just bigger numbers."
                    value={config.difficulty}
                    onChange={(v) => setConfig(resolveConfig({ difficulty: v as Difficulty }))}
                    options={difficultyOptions}
                  />
                  <SelectField
                    label="Question type"
                    value={config.questionType}
                    onChange={(v) => setConfig(resolveConfig({ questionType: v as QuestionType }))}
                    options={questionTypeOptions}
                  />
                </div>
                <SelectField
                  label="Number of questions (max 10)"
                  hint="Short sets, repeated often, beat one long set."
                  value={config.questionCount.toString()}
                  onChange={(v) => setConfig({ ...config, questionCount: Math.min(10, Math.max(1, parseInt(v, 10) || 5)) })}
                  options={["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"].map((n) => ({ value: n, label: `${n} question${n === "1" ? "" : "s"}` }))}
                />
              </ConfigSection>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={primaryAction}
                disabled={isGenerating}
                size="lg"
                className="min-h-[52px] flex-1 sm:flex-none gap-2 text-base font-semibold"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                    {phase || "Generating…"}
                  </>
                ) : (
                  <>
                    <Settings2 className="h-5 w-5" aria-hidden="true" />
                    {primaryLabel}
                  </>
                )}
              </Button>
              {questions.length > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleCopy}
                    className="min-h-[52px] flex-1 sm:flex-none gap-2 text-base font-semibold"
                  >
                    <Copy className="h-5 w-5" aria-hidden="true" />
                    {copied ? "Copied!" : "Copy Set"}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleDownloadPDF}
                    className="min-h-[52px] flex-1 sm:flex-none gap-2 text-base font-semibold"
                  >
                    <Download className="h-5 w-5" aria-hidden="true" />
                    Download PDF
                  </Button>
                </>
              )}
            </div>
          </section>

          {error && (
            <div className="rounded-lg border border-[var(--color-error-300)] bg-[var(--color-error-50)] p-4" role="alert">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-[var(--color-error-700)] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-[var(--color-error-900)]">{error}</p>
              </div>
            </div>
          )}

          {questions.length > 0 && (
            <section aria-label="Your practice worksheet" className="space-y-0">
              <Card className="overflow-hidden border-[var(--color-border)]">
                <div className="border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-5 py-4 sm:px-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-text-muted)]">
                        Practice Worksheet &middot; Set #{seed}
                      </p>
                      <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-[var(--color-text)]">
                        <FileText className="h-5 w-5 text-[var(--color-accent)]" aria-hidden="true" />
                        {topicName} &mdash; {levelName}
                      </h2>
                      <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">
                        {difficultyName} &middot; {questions.length} question{questions.length === 1 ? "" : "s"} &middot;
                        suggested time {Math.max(5, questions.length * 3)} min
                        {lastStats && (lastStats.rejected + lastStats.duplicates > 0)
                          ? ` &middot; ${lastStats.rejected + lastStats.duplicates} invalid draft(s) discarded`
                          : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                      <label className="inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-sm font-medium text-[var(--color-text)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showAnswers}
                          onChange={(e) => setShowAnswers(e.target.checked)}
                          className="h-4 w-4 rounded border-[var(--color-border)] accent-[var(--color-accent)]"
                        />
                        Show answers
                      </label>
                      <Button variant="outline" size="sm" onClick={handleCopy} className="min-h-[44px] gap-2">
                        <Copy className="h-4 w-4" aria-hidden="true" />
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="min-h-[44px] gap-2">
                        <Download className="h-4 w-4" aria-hidden="true" />
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>

                <ol className="divide-y divide-[var(--color-border)] p-4 sm:p-6">
                  {questions.map((q, i) => {
                    const options = q.metadata?.options as string[] | undefined;
                    const choices = q.questionType === "multiple-choice" && options?.length ? options.slice(0, 4) : null;
                    const correctIndex = choices && q.answer ? choices.indexOf(q.answer) : -1;
                    return (
                      <li key={q.id} className="py-5 first:pt-0 last:pb-0">
                        <div className="flex items-start gap-4">
                          <span
                            aria-hidden="true"
                            className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-accent-hover)] text-sm font-bold text-[var(--color-accent-fg)] shadow-sm"
                          >
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1 space-y-4">
                            <div className="text-base font-medium leading-relaxed text-[var(--color-text)]">
                              {q.latex ? (
                                <Suspense fallback={<p className="whitespace-pre-wrap">{q.question}</p>}>
                                  <MathNotation latex={q.latex} fallback={q.question} />
                                </Suspense>
                              ) : (
                                <p className="whitespace-pre-wrap">{q.question}</p>
                              )}
                            </div>

                            {choices && (
                              <ol className="grid gap-2.5 sm:grid-cols-2" aria-label="Answer choices">
                                {choices.map((opt, oi) => {
                                  const isCorrect = showAnswers && oi === correctIndex;
                                  return (
                                    <li
                                      key={oi}
                                      className={`flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors ${
                                        isCorrect
                                          ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)] font-semibold text-[var(--color-text)] shadow-[0_0_0_1px_var(--color-accent)]"
                                          : "border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] hover:border-[var(--color-accent)]/50"
                                      }`}
                                    >
                                      <span
                                        aria-hidden="true"
                                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                                          isCorrect
                                            ? "bg-[var(--color-accent)] text-[var(--color-accent-fg)]"
                                            : "bg-[var(--color-surface-2)] text-[var(--color-text-muted)]"
                                        }`}
                                      >
                                        {["A", "B", "C", "D"][oi]}
                                      </span>
                                      <span className="min-w-0 flex-1 break-words leading-relaxed">
                                        {opt}
                                        {isCorrect && (
                                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-normal bg-[var(--color-accent)]/20 text-[var(--color-accent)]">
                                            <CheckCircle className="h-3 w-3" aria-hidden="true" />
                                            Correct
                                          </span>
                                        )}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ol>
                            )}

                            {showAnswers && (q.answer || q.solution) && (
                              <div className="space-y-3 border-t border-dashed border-[var(--color-border)] pt-4 bg-[var(--color-surface)]/50 rounded-lg p-4">
                                {q.answer && (
                                  <p className="flex items-start gap-3 text-sm">
                                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)]/10 text-[var(--color-accent)]">
                                      <CheckCircle className="h-4 w-4" aria-hidden="true" />
                                    </div>
                                    <span className="text-[var(--color-text)] pt-0.5">
                                      <strong>Answer: </strong>
                                      {q.answer}
                                    </span>
                                  </p>
                                )}
                                {q.solution && <SolutionSteps solution={q.solution} answer={q.answer} />}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>

                <div className="border-t border-[var(--color-border)] bg-[var(--color-surface-2)] px-5 py-4 sm:px-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button onClick={handleNextSet} disabled={isGenerating} className="min-h-[48px] gap-2">
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      Generate another set
                    </Button>
                    <p className="text-xs text-[var(--color-text-muted)] flex-1 min-w-[200px] text-center sm:text-left">
                      Same settings, fresh questions &mdash; previous-set duplicates excluded.
                    </p>
                  </div>
                </div>
              </Card>
            </section>
          )}

          {questions.length === 0 && !isGenerating && (
            <section className="py-16 text-center" aria-label="Get started">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--color-accent-soft)] text-[var(--color-accent)] mb-5" aria-hidden="true">
                <Sparkles className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-semibold text-[var(--color-text)]">Create your mathematics practice set</h2>
              <p className="mx-auto mt-3 max-w-md text-base text-[var(--color-text-muted)]">
                Choose your level and topic above, then generate. Every question
                comes with a verified answer and a step-by-step worked solution.
              </p>
              <p className="mt-8 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-muted)]">
                Or jump straight in with a topic
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2.5">
                {topics.slice(0, 8).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setConfig(resolveConfig({ topic: t.id as MathTopic }));
                      window.setTimeout(() => void runGeneration(seed + 1, []), 0);
                    }}
                    className="min-h-[48px] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-5 text-sm font-medium text-[var(--color-accent)] transition-all duration-150 hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)] hover:shadow-[0_0_0_1px_var(--color-accent)]"
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="rounded-2xl border border-[var(--color-accent)] bg-[var(--color-accent-soft)] p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--color-accent)] text-[var(--color-accent-fg)]" aria-hidden="true">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-[var(--color-accent)]">Privacy first — always</h3>
                <p className="mt-1.5 text-sm text-[var(--color-text)]">
                  Generation, validation, copy, and PDF export all run in your
                  browser. Nothing is uploaded, and no account is needed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-[var(--color-border)] bg-[var(--color-surface)] py-12 mt-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-3 max-w-5xl mx-auto">
            <div>
              <h3 className="font-semibold text-[var(--color-text)] mb-3">For Students</h3>
              <ul className="space-y-2 text-sm text-[var(--color-text-muted)]">
                <li>Practice for exams & tests</li>
                <li>Self-study with instant feedback</li>
                <li>Multiple difficulty levels</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-[var(--color-text)] mb-3">For Teachers</h3>
              <ul className="space-y-2 text-sm text-[var(--color-text-muted)]">
                <li>Create worksheets in seconds</li>
                <li>Differentiated practice sets</li>
                <li>Export to PDF for printing</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-[var(--color-text)] mb-3">Supported Systems</h3>
              <ul className="space-y-2 text-sm text-[var(--color-text-muted)]">
                <li>India (CBSE, ICSE, JEE, NEET)</li>
                <li>United States (Common Core, AP)</li>
                <li>General / Topic-based</li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-10 border-t border-[var(--color-border)] text-center text-sm text-[var(--color-text-muted)]">
            <p>Part of <a href={siteConfig.mainSiteUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--color-accent)] hover:underline">DevStackIO</a> &mdash; Free online developer tools</p>
          </div>
        </div>
      </footer>
    </div>
  );
}